import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { ArrowRight, Sparkles, Clock, Users, ChevronRight, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Central booking hub on dcaenterprisesinc.com (/coaching).
 * Lists all active booking brands David coaches under, lets visitors pick
 * one and either book inline OR jump straight to the dedicated brand site.
 *
 * Free Business Jumpstart Session is NOT shown here - it has its own URLs
 * (/free-jumpstart-session and /schedule).
 */
export default function CoachingHubPage() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/booking-brands`)
      .then((r) => setBrands(r.data))
      .catch(() => setBrands([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout
      title="Book a Coaching Session with David - All Brands"
      description="Book directly with David C. Ashley across his coaching brands: business consulting, career coaching, wellness, and life & purpose coaching."
    >
      <div className="bg-[#FAFAFA] min-h-screen" data-testid="coaching-hub-page">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] text-white py-14 sm:py-20">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
          <div className="absolute -top-10 -left-10 w-80 h-80 rounded-full bg-[#1D4ED8] opacity-30 blur-3xl"></div>
          <div className="absolute -bottom-10 -right-10 w-80 h-80 rounded-full bg-[#F97316] opacity-20 blur-3xl"></div>

          <div className="container-custom relative z-10 text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-xs sm:text-sm font-semibold rounded-full mb-4" data-testid="hub-badge">
              <Users size={14} className="text-[#FB923C]" /> All Coaching Brands · One Calendar
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight" data-testid="hub-title">
              Book a Session with{' '}
              <span className="bg-gradient-to-r from-[#60A5FA] to-[#F97316] bg-clip-text text-transparent">David</span>
            </h1>
            <p className="text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-2xl mx-auto">
              Pick the area you need help in. Business, career, wellness, or life &amp; purpose. Book directly with David below. One calendar, one easy booking flow.
            </p>
          </div>
        </section>

        {/* Brand grid */}
        <section className="container-custom py-12 sm:py-16">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-10 h-10 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="brand-grid">
              {brands.map((b) => (
                <BrandCard key={b.brand_id} brand={b} />
              ))}
            </div>
          )}
        </section>

        {/* Note about Free Jumpstart */}
        <section className="bg-white border-t border-[#E2E8F0] py-10" data-testid="hub-note">
          <div className="container-custom max-w-3xl text-center">
            <Sparkles size={32} className="text-[#F97316] mx-auto mb-3" />
            <h2 className="text-xl font-bold text-[#0A0F1C] mb-2">Just looking for the FREE 20-Minute Business Jumpstart?</h2>
            <p className="text-sm text-[#475569] mb-4">That one has its own dedicated page just for aspiring entrepreneurs.</p>
            <Link to="/free-jumpstart-session" className="btn-primary-orange inline-flex items-center gap-2" data-testid="hub-free-jumpstart-cta">
              Book FREE 20-Min Jumpstart <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}

function BrandCard({ brand }) {
  const featured = brand.services?.find((s) => s.is_featured) || brand.services?.[0];
  const theme = { primary: brand.primary_color, accent: brand.accent_color, bg: brand.bg_color };

  return (
    <div
      className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col"
      data-testid={`brand-card-${brand.slug}`}
    >
      <div className="px-6 py-5" style={{ background: theme.bg, color: '#fff' }}>
        <div className="text-[10px] font-bold tracking-[2px] uppercase mb-1" style={{ color: theme.accent }}>
          {brand.name}
        </div>
        <h3 className="font-bold text-lg leading-tight">{brand.tagline}</h3>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-sm text-[#475569] mb-4 leading-relaxed flex-1">{brand.description}</p>
        {featured && (
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 mb-4">
            <div className="flex items-baseline justify-between mb-1">
              <div className="font-bold text-[#0A0F1C] text-sm">{featured.name}</div>
              <div className="font-bold text-sm" style={{ color: featured.price === 0 ? theme.accent : theme.primary }}>
                {featured.price === 0 ? 'FREE' : `$${featured.price}`}
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-[#475569]">
              <Clock size={11} /> {featured.duration_min} min
            </div>
          </div>
        )}
        <Link
          to={`/coaching/${brand.slug}`}
          className="inline-flex items-center justify-center gap-2 w-full text-white font-bold text-sm px-4 py-2.5 rounded-lg hover:opacity-90 transition-opacity mb-2"
          style={{ background: theme.primary }}
          data-testid={`brand-card-book-${brand.slug}`}
        >
          Book Now <ArrowRight size={14} />
        </Link>
        {brand.website_url && (
          <a
            href={brand.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1 text-xs font-semibold text-[#475569] hover:text-[#1D4ED8] py-2"
            data-testid={`brand-card-website-${brand.slug}`}
          >
            Visit {brand.name} <ExternalLink size={11} />
          </a>
        )}
        <div className="mt-3 pt-3 border-t border-[#F1F5F9] text-xs text-[#94A3B8] inline-flex items-center gap-1">
          {brand.services?.length || 0} session option{(brand.services?.length || 0) === 1 ? '' : 's'} <ChevronRight size={11} />
        </div>
      </div>
    </div>
  );
}
